import { useState } from 'react';
import {
    FileText,
    Settings,
    BarChart,
    PieChart,
    ArrowUp,
    ArrowDown,
    Check,
    Shield,
    Zap,
    Lock,
    File,
    Award
} from 'lucide-react';
const Main = () => {
    const rfpScorecard = {
        criteria: [
            {
                name: "Technical Expertise",
                weight: 30,
                subcriteria: [
                    { name: "Team Qualifications", weight: 40 },
                    { name: "Industry Experience", weight: 30 },
                    { name: "Innovation Approach", weight: 30 }
                ]
            },
            {
                name: "Project Approach",
                weight: 25,
                subcriteria: [
                    { name: "Methodology", weight: 35 },
                    { name: "Timeline", weight: 35 },
                    { name: "Risk Management", weight: 30 }
                ]
            },
            {
                name: "Value & Pricing",
                weight: 25,
                subcriteria: [
                    { name: "Cost Structure", weight: 40 },
                    { name: "Value Add Services", weight: 30 },
                    { name: "Payment Terms", weight: 30 }
                ]
            },
            {
                name: "Track Record",
                weight: 20,
                subcriteria: [
                    { name: "Similar Projects", weight: 40 },
                    { name: "Client References", weight: 30 },
                    { name: "Success Metrics", weight: 30 }
                ]
            }
        ]
    };

    // Mock legal tech integrations
    const techIntegrations = [
        {
            name: "ContractFlow",
            type: "Contract Generation",
            features: ["Template Library", "Automated Assembly", "Version Control"],
            matchScore: 95
        },
        {
            name: "ComplianceCheck",
            type: "Compliance",
            features: ["Real-time Monitoring", "Risk Alerts", "Audit Reports"],
            matchScore: 88
        },
        {
            name: "LegalEnforce",
            type: "Enforcement",
            features: ["Automated Notices", "Case Tracking", "Document Collection"],
            matchScore: 92
        }
    ];

    // Mock competitive insights
    const competitiveInsights = {
        market: {
            position: "Top 25%",
            trend: "upward",
            opportunities: [
                "AI/ML expertise demand growing",
                "Gap in rapid response services",
                "Cross-border capability needs"
            ]
        },
        pricing: {
            position: "Mid-range",
            suggestions: [
                "Value-based pricing for AI work",
                "Bundle compliance services",
                "Introduce success fees"
            ]
        },
        service: {
            gaps: [
                "24/7 emergency response",
                "Automated status updates",
                "Integrated compliance tools"
            ],
            improvements: [
                "Add real-time reporting",
                "Enhance digital delivery",
                "Expand tech partnerships"
            ]
        }
    };
    return (
        <>
            <div className="space-y-6">
                {/* RFP Management */}
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div>
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            <span className='font-bold'> RFP Scorecard</span>
                        </div>
                        <div>
                            Automated bid evaluation aligned with client criteria
                        </div>
                    </div>
                    <div>
                        <div className="space-y-6 mt-2">
                            {rfpScorecard.criteria.map((criterion, index) => (
                                <div key={index} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h3 className="font-medium">{criterion.name}</h3>
                                            <p className="text-sm text-gray-500">Weight: {criterion.weight}%</p>
                                        </div>
                                        <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                            {criterion.subcriteria.length} Subcriteria
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {criterion.subcriteria.map((sub, idx) => (
                                            <div key={idx} className="flex justify-between items-center">
                                                <div className="text-sm">{sub.name}</div>
                                                <div className="flex items-center gap-4">
                                                    <div className="text-sm text-gray-500">{sub.weight}%</div>
                                                    <div className="bg-gray-200 rounded-full h-2.5 w-24">
                                                        <div className="bg-green h-2 rounded-full" style={{ width: `45%` }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Legal Tech Integrations */}
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div>
                        <div className="flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            <span className='font-bold'>Legal Tech Ecosystem</span>
                        </div>
                        <div>
                            Integrated tools and services
                        </div>
                    </div>
                    <div>
                        <div className="grid grid-cols-3 gap-4 mt-2">
                            {techIntegrations.map((tech, index) => (
                                <div key={index} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h3 className="font-medium">{tech.name}</h3>
                                            <p className="text-sm text-gray-500">{tech.type}</p>
                                        </div>
                                        <div className={`text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm
                    ${tech.matchScore >= 90 ? "bg-[#d1fae5] text-[#065f46]" :
                                                "bg-blue-100 text-blue-800"}
                  `}>
                                            {tech.matchScore}% Match
                                        </div>
                                    </div>

                                    <div className="space-y-2 mt-4">
                                        {tech.features.map((feature, idx) => (
                                            <div key={idx} className="flex items-center gap-2 text-sm">
                                                <Check className="w-4 h-4 text-[#22c55e]" />
                                                {feature}
                                            </div>
                                        ))}
                                    </div>

                                    <button className="w-full mt-4 flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                        Enable Integration
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Competitive Intelligence */}
                <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                    <div>
                        <div className="flex items-center gap-2">
                            <BarChart className="w-5 h-5" />
                            <span className='font-bold'> Market Intelligence</span>
                        </div>
                        <div>
                            Data-driven competitive insights
                        </div>
                    </div>
                    <div>
                        <div className="grid grid-cols-3 gap-4 mt-2">
                            {/* Market Position */}
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-medium">Market Position</h3>
                                    <div className="text-xs flex items-center font-medium me-2 px-2.5 py-0.5 rounded-sm bg-[#d1fae5] text-[#065f46]">
                                        <ArrowUp className="w-3 h-3 mr-1" />
                                        Rising
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {competitiveInsights.market.opportunities.map((opp, index) => (
                                        <div key={index} className="text-sm flex items-start gap-2">
                                            <div className="w-1 h-1 bg-blue-500 rounded-full mt-2"></div>
                                            {opp}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Pricing Strategy */}
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-medium">Pricing Optimization</h3>
                                    <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                        Recommendations
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {competitiveInsights.pricing.suggestions.map((sugg, index) => (
                                        <div key={index} className="text-sm flex items-start gap-2">
                                            <div className="w-1 h-1 bg-blue-500 rounded-full mt-2"></div>
                                            {sugg}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Service Enhancement */}
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-medium">Service Gaps</h3>
                                    <div className='text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800'>
                                        Opportunities
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {competitiveInsights.service.improvements.map((imp, index) => (
                                        <div key={index} className="text-sm flex items-start gap-2">
                                            <div className="w-1 h-1 bg-blue-500 rounded-full mt-2"></div>
                                            {imp}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                            <h3 className="font-medium mb-3">Recommended Actions</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <button className="justify-start flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                    <Award className="w-4 h-4 mr-2" />
                                    Update Service Offerings
                                </button>
                                <button className="justify-start flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg">
                                    <PieChart className="w-4 h-4 mr-2" />
                                    Review Pricing Model
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default Main